<script lang="ts" setup>
import type { Component } from 'vue';
import type { AnyItem, CompositePrimaryKey, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { RelatedItemOperator } from './utils/related-item-match';
import { createReusableTemplate } from '@vueuse/core';
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayTemplateFieldSelection } from '#layers/blocks/public';
import { useItems } from '#layers/collection-query/public';
import { buildFilterFromItemKeys, CollectionReads, CURRENT_USER_OPTION, FilterOperator, isCurrentUserKey, isSameKey, pickByPrimitiveFields, stringifyStableId } from '#layers/data-model/public';
import FilterRelatedItemDrawerPicker from './FilterRelatedItemDrawerPicker.vue';
import FilterRelatedItemLabel from './FilterRelatedItemLabel.vue';
import FilterRelatedItemPopover from './FilterRelatedItemPopover.vue';

const { operator, keys, oppositeCollection, keyFields, relationMatchPicker, relationMatchPreview, relationMatchPreviewFields, allowCurrentUser = false, autoOpen, disabled = false, readonly = false } = defineProps<{
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
	oppositeCollection: ResolvedMonospaceCollection;
	/** The fields of `oppositeCollection` each key carries: its primary key, or the fields a foreign key references. */
	keyFields: ResolvedMonospacePrimitiveField[];
	relationMatchPicker?: Component;
	relationMatchPreview?: Component;
	relationMatchPreviewFields?: readonly string[];
	allowCurrentUser?: boolean;
	autoOpen?: number;
	disabled?: boolean;
	readonly?: boolean;
}>();

const emit = defineEmits<{
	'update:keys': [keys: CompositePrimaryKey[]];
}>();

const { t } = useI18n();

const [DefineValue, ReuseValue] = createReusableTemplate();

const picker = computed(() => relationMatchPicker ? FilterRelatedItemPopover : FilterRelatedItemDrawerPicker);

const isEditable = computed(() => !disabled && !readonly);

// `_in` / `_nin` pick a list of items; `_eq` / `_neq` pick a single item.
const isList = computed(() => operator === FilterOperator.IN || operator === FilterOperator.NIN);

// Number of selected items shown inline (comma-separated) before the rest collapse into a "+N" count.
const VISIBLE_KEY_COUNT = 3;

const visibleKeys = computed(() => keys.slice(0, VISIBLE_KEY_COUNT));
const overflowCount = computed(() => Math.max(0, keys.length - VISIBLE_KEY_COUNT));

// The pinned "Current User" selection isn't a real record, so it's excluded from the fetch and labelled directly.
const resolvableKeys = computed(() => keys.filter((key) => !isCurrentUserKey(key)));

function keyFallbackLabel(key: CompositePrimaryKey): string {
	if (isCurrentUserKey(key) && allowCurrentUser) return t(CURRENT_USER_OPTION.labelKey);
	return keyFields.map((field) => String(key[field.apiName])).join(', ');
}

const fieldSelection = useDisplayTemplateFieldSelection(computed(() => oppositeCollection), CollectionReads.Many);

const { items, isSettled, showLoading } = useItems({
	enabled: () => resolvableKeys.value.length > 0,
	collection: () => oppositeCollection,
	pageSize: () => Math.max(resolvableKeys.value.length, 1),
	query: () => ({
		filter: buildFilterFromItemKeys({ keys: resolvableKeys.value, primaryKeyFields: keyFields }),
		...fieldSelection.value,
		fields: [...new Set([...(fieldSelection.value.fields ?? ['*']), ...keyFields.map((field) => field.apiName), ...(relationMatchPreviewFields ?? [])])],
	}),
});

// Remember every resolved item across refetches. Removing a key rebuilds the
// query, which briefly empties `items`; without this cache the remaining labels
// would flash their loading skeleton on each removal.
const itemsByKey = reactive(new Map<string, AnyItem>());

watch([items, isSettled], ([list, settled]) => {
	if (settled) itemsByKey.clear();

	for (const item of list) {
		itemsByKey.set(stringifyStableId(pickByPrimitiveFields(item, keyFields)), item);
	}
}, { immediate: true });

function findItem(key: CompositePrimaryKey) {
	return itemsByKey.get(stringifyStableId(key));
}

const matches = computed(() => keys.map((key) => ({ key, item: findItem(key), fallback: keyFallbackLabel(key) })));

function onKeysPicked(next: CompositePrimaryKey[]) {
	emit('update:keys', next);
}

function onRemove(key: CompositePrimaryKey) {
	if (!isEditable.value) return;

	emit('update:keys', keys.filter((candidate) => !isSameKey(candidate, key)));
}
</script>

<template>
	<component
		:is="picker"
		:keys
		:opposite-collection
		:key-fields
		:popover="relationMatchPicker"
		:allow-current-user="Boolean(relationMatchPicker) && allowCurrentUser"
		:auto-open
		:disabled
		:readonly
		@update:keys="onKeysPicked"
	>
		<DefineValue>
			<span data-test="related-item-value" class="flex h-full min-w-0 items-center gap-1 px-2 text-xs">
				<component
					:is="relationMatchPreview"
					v-if="relationMatchPreview"
					:matches
					:collection-id="oppositeCollection.id"
					:is-loading="showLoading"
				/>
				<template v-else>
					<span class="flex min-w-0 items-center gap-1">
						<span
							v-for="(key, index) in visibleKeys"
							:key="stringifyStableId(key)"
							class="flex min-w-0 items-center"
						>
							<FilterRelatedItemLabel
								:item="findItem(key)"
								:collection-id="oppositeCollection.id"
								:fallback="keyFallbackLabel(key)"
								:is-loading="showLoading"
								layout="fit-content"
							/>
							<span v-if="index < visibleKeys.length - 1">,</span>
						</span>
					</span>
					<DBadge v-if="overflowCount > 0" color="neutral" variant="subtle" size="sm">
						+{{ overflowCount }}
					</DBadge>
				</template>
			</span>
		</DefineValue>

		<span
			v-if="keys.length === 0"
			class="px-2 text-xs whitespace-nowrap text-muted"
		>
			{{ $t(isList ? 'common.select_items' : 'common.select_item') }}
		</span>

		<ReuseValue v-else-if="keys.length === 1" />

		<DPopover
			v-else-if="!relationMatchPreview"
			mode="hover"
			:open-delay="0"
			:close-delay="0"
			:content="{ side: 'bottom', align: 'start', sideOffset: 4 }"
			:ui="{ content: 'max-h-60 w-56 overflow-y-auto p-1' }"
		>
			<ReuseValue />

			<template #content>
				<div
					v-for="key in keys"
					:key="stringifyStableId(key)"
					class="flex w-full min-w-0 items-center gap-2 rounded-sm p-1 ps-3"
				>
					<FilterRelatedItemLabel
						class="min-w-0 truncate text-xs font-medium text-default"
						:item="findItem(key)"
						:collection-id="oppositeCollection.id"
						:fallback="keyFallbackLabel(key)"
						:is-loading="showLoading"
					/>
					<DButton
						v-if="isEditable"
						class="ms-auto flex shrink-0 cursor-pointer items-center text-dimmed hover:text-default"
						:aria-label="$t('common.remove')"
						variant="ghost"
						color="neutral"
						size="sm"
						icon="lucide:x"
						:ui="{ leadingIcon: 'size-3' }"
						@click.stop="onRemove(key)"
					/>
				</div>
			</template>
		</DPopover>

		<ReuseValue v-else />
	</component>
</template>
